import { ApiError, get, send } from "./api.js";

/**
 * Files on a lesson write-up.
 *
 * One client for both teaching domains. The caller supplies the base path —
 * `/admin/students/:id/lessons/:lessonId/attachments` for a private lesson,
 * `/school-domain/lesson-workspace/notes/:noteId/attachments` for a School one
 * — because that base is the only thing that differs, and the Hub decides
 * authority behind either of them.
 */

export interface LessonNoteAttachment {
  id: string;
  fileName: string;
  fileType: string | null;
  fileSize: number | null;
  /** `pdf` | `image` | `audio` | `video` | `file` | `link`, as the Hub classified it. */
  resourceType: string | null;
  uploadedAt?: string;
  shareable?: boolean;
}

export interface LessonNoteAttachmentList {
  attachments: LessonNoteAttachment[];
  /**
   * Whether object storage is configured at all. Without it the page says so
   * rather than offering a picker whose upload can only fail.
   */
  storageConfigured: boolean;
}

/**
 * What the file picker offers, matched to the Hub's allowlist. Formats a
 * browser may execute rather than merely display — SVG, HTML, archives — are
 * deliberately absent from both ends.
 */
export const LESSON_ATTACHMENT_ACCEPT =
  "application/pdf,image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif," +
  "audio/mpeg,audio/mp4,audio/x-m4a,audio/aac,audio/wav,audio/x-wav,audio/ogg,audio/webm,audio/flac," +
  "video/mp4,video/webm,video/quicktime";

/** Kept in step with `MAX_RESOURCE_BYTES` on the Hub. */
export const MAX_LESSON_ATTACHMENT_MB = 50;

export function listLessonNoteAttachments(base: string): Promise<LessonNoteAttachmentList> {
  return get<LessonNoteAttachmentList>(base);
}

/**
 * Send one file.
 *
 * No `Content-Type` of our own: only the browser knows the multipart boundary
 * it is about to generate.
 */
export async function uploadLessonNoteAttachment(
  base: string,
  file: File,
): Promise<LessonNoteAttachment> {
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`/api${base}`, {
    method: "POST",
    headers: { Accept: "application/json" },
    body: form,
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      body && typeof body === "object" && "error" in body && typeof body.error === "string"
        ? body.error
        : `That file could not be attached (${response.status}).`;
    throw new ApiError(response.status, message);
  }
  return (body as { attachment: LessonNoteAttachment }).attachment;
}

export interface LessonNoteAttachmentRemoval {
  removed: string;
  removedHref: string;
  previousUpdatedAt: string;
  updatedAt: string;
}

export function removeLessonNoteAttachment(base: string, attachmentId: string): Promise<LessonNoteAttachmentRemoval> {
  return send("DELETE", `${base}/${encodeURIComponent(attachmentId)}`);
}

export function setLessonNoteAttachmentSharing(base: string, attachmentId: string, shareable: boolean): Promise<{ id: string; shareable: boolean }> {
  return send("PATCH", `${base}/${encodeURIComponent(attachmentId)}/sharing`, { shareable });
}

/** Human sizes, for a line under a file name. */
export function formatAttachmentSize(bytes: number | null): string | null {
  if (bytes === null || !Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

/** A word for what this is, for somebody scanning a list of file names. */
export function describeAttachmentKind(attachment: LessonNoteAttachment): string {
  switch (attachment.resourceType) {
    case "pdf":
      return "PDF";
    case "image":
      return "Image";
    case "audio":
      return "Audio";
    case "video":
      return "Video";
    case "link":
      return "Web link";
    default:
      return "File";
  }
}
